import { useState, useEffect, useRef, useCallback } from 'react';
import browserCacheService from '../services/BrowserCacheService';

/**
 * Prüft, ob ein Fehler einen automatischen Retry rechtfertigt
 * (transiente Netzwerk-/Server-Fehler, keine Auth-/Client-Fehler)
 */
const isRetryableError = (error) =>
  error?.code === 'ERR_NETWORK' ||
  !navigator.onLine ||
  (error?.response?.status >= 500);

/**
 * Stale-While-Revalidate Daten-Hook auf Basis des BrowserCacheService.
 *
 * Verhalten:
 * - Cache-Hit: Daten werden SOFORT angezeigt (kein Spinner), im Hintergrund
 *   wird neu geladen und still aktualisiert (isRefreshing).
 * - Cache-Miss: Normales Laden mit isLoading (Seite zeigt Skeleton/Spinner).
 * - Transiente Fehler (offline, 5xx) werden mit exponentiellem Backoff
 *   automatisch erneut versucht (1s, 2s, 4s - max. maxAttempts Versuche).
 *
 * @param {string|null} cacheKey - Eindeutiger Cache-Key (null = noch nicht bereit, z.B. User lädt noch)
 * @param {Function} fetcher - Async-Funktion, die die Daten lädt
 * @param {number} ttl - Cache-Lebensdauer in ms (z.B. browserCacheService.TTL.SESSIONS)
 * @param {Object} [options]
 * @param {number} [options.maxAttempts=3] - Maximale Ladeversuche bei transienten Fehlern
 * @returns {{ data, isLoading, isRefreshing, error, retryCount, refresh }}
 */
export function useCachedData(cacheKey, fetcher, ttl, { maxAttempts = 3 } = {}) {
  const [data, setData] = useState(() => (cacheKey ? browserCacheService.get(cacheKey) : null));
  const [isLoading, setIsLoading] = useState(() => (cacheKey ? browserCacheService.get(cacheKey) == null : true));
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Fetcher in Ref halten, damit Inline-Funktionen den Effect nicht neu triggern
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const dataRef = useRef(data);
  dataRef.current = data;

  // Generation-Guard: Wenn sich der cacheKey ändert (z.B. anderer User),
  // dürfen Ergebnisse alter Requests den neuen State nicht überschreiben
  const generationRef = useRef(0);
  const retryTimeoutRef = useRef(null);

  const load = useCallback(async (attempt, { background, generation }) => {
    if (background) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }

    try {
      const result = await fetcherRef.current();
      if (generationRef.current !== generation) return;

      setData(result);
      setError(null);
      setRetryCount(0);
      setIsLoading(false);
      setIsRefreshing(false);
      browserCacheService.set(cacheKey, result, ttl);
    } catch (err) {
      if (generationRef.current !== generation) return;

      // Fehler sofort melden (UI zeigt z.B. "wir versuchen es erneut..."),
      // bei Erfolg eines Retries wird er wieder gelöscht
      setError(err);

      if (isRetryableError(err) && attempt < maxAttempts - 1) {
        const retryDelay = Math.min(1000 * Math.pow(2, attempt), 5000);
        console.log(`🔄 useCachedData: Retry in ${retryDelay}ms (Versuch ${attempt + 2}/${maxAttempts})...`);
        setRetryCount(attempt + 1);
        retryTimeoutRef.current = setTimeout(() => load(attempt + 1, { background, generation }), retryDelay);
        return; // isLoading/isRefreshing bleiben aktiv während des Retries
      }

      // retryCount NICHT zurücksetzen: UI nutzt ihn, um nach erschöpften
      // Versuchen den manuellen "Erneut versuchen"-Button anzuzeigen
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [cacheKey, ttl, maxAttempts]);

  useEffect(() => {
    if (!cacheKey) return undefined;

    const generation = ++generationRef.current;
    const cached = browserCacheService.get(cacheKey);

    if (cached != null) {
      // Stale-While-Revalidate: sofort anzeigen, still im Hintergrund aktualisieren
      setData(cached);
      setIsLoading(false);
      load(0, { background: true, generation });
    } else {
      setData(null);
      load(0, { background: false, generation });
    }

    return () => {
      generationRef.current++;
      clearTimeout(retryTimeoutRef.current);
    };
  }, [cacheKey, load]);

  const refresh = useCallback(() => {
    setError(null);
    setRetryCount(0);
    return load(0, { background: dataRef.current != null, generation: generationRef.current });
  }, [load]);

  return { data, isLoading, isRefreshing, error, retryCount, refresh };
}

export default useCachedData;
